import {useEffect, useMemo} from 'react';

import {AnyFunction} from '~shared/types';

import {debounce, throttle} from '~ui/utils';
import {batchUpdates} from '~ui/services/react';

import {useLatest} from './use-latest';

export function useDebounce<Fn extends AnyFunction>(cb: Fn, wait: number) {
    const latestCb = useLatest(cb);

    const debouncedFn = useMemo(
        () =>
            debounce((...args: Parameters<Fn>) => {
                // updates in async callbacks are not
                // batched by default, so we do it by hand
                // till it will be added to react
                // https://github.com/reactwg/react-18/discussions/21
                batchUpdates(() => {
                    latestCb.current.apply(null, args);
                });
            }, wait),
        [latestCb, wait],
    );

    // cancel function on unmount
    useEffect(() => () => debouncedFn.cancel(), [debouncedFn]);

    return debouncedFn;
}

export function useThrottle<Fn extends AnyFunction>(cb: Fn, wait: number) {
    const latestCb = useLatest(cb);

    const debouncedFn = useMemo(
        () =>
            throttle((...args: Parameters<Fn>) => {
                // updates in async callbacks are not
                // batched by default, so we do it by hand
                // till it will be added to react
                // https://github.com/reactwg/react-18/discussions/21
                batchUpdates(() => {
                    latestCb.current.apply(null, args);
                });
            }, wait),
        [latestCb, wait],
    );

    // cancel function on unmount
    useEffect(() => () => debouncedFn.cancel(), [debouncedFn]);

    return debouncedFn;
}
